// App.jsx — Giant Valley JM root. Auth gate + navigation + API-backed persistence.
// (Ported from the prototype's app.jsx: the fake iPhone frame is gone, localStorage is
//  replaced by the backend, and a login screen guards the app.)
import React, { useState, useEffect, useRef } from 'react';
import { api, AuthError } from './lib/api.js';
import { Icon, TabBar, COMPANY, TEMPLATES, TODAY, fmtDateShort, nextGroupId, ARTICLES, setArticles, setCompany, setVat } from './shared.js';
import { TodayScreen, InvoiceScreen, ProfileScreen } from './screens/screens-main.jsx';
import { JobDetail, NavigateScreen } from './screens/screens-flow.jsx';
import { CreateFlow, ReportScreen } from './screens/screens-create-report.jsx';
import { CalendarScreen, FreeSlotsScreen, SlotPickerSheet } from './screens/screens-calendar.jsx';
import { TemplatesScreen } from './screens/screens-templates.jsx';
import { ArticlesScreen } from './screens/screens-articles.jsx';
import { AuthScreen } from './screens/AuthScreen.jsx';

const DEFAULT_SETTINGS = { lunchOn: true, lunchStart: '12:00', lunchMinutes: 30, travelOn: true, travelDefault: 15 };

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function nextJobId(jobs) {
  const max = jobs.reduce((m, j) => Math.max(m, parseInt((j.id.split('-')[1]) || 0, 10)), 1044);
  return 'U-' + (max + 1);
}
function nextInvoiceNo(jobs) {
  const max = jobs.reduce((m, j) => j.invoiceNo ? Math.max(m, parseInt(j.invoiceNo.split('-')[1] || 0, 10)) : m, 2450);
  return 'F-' + (max + 1);
}
function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

// ── Toast ───────────────────────────────────────────────────
function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ position: 'absolute', left: 18, right: 18, bottom: 120, zIndex: 300, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
      <div className="gv-fade" style={{ background: 'var(--ink)', color: '#fff', borderRadius: 13, padding: '13px 18px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 28px rgba(0,0,0,.28)', fontSize: 14.5, fontWeight: 600, maxWidth: '90%' }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="check" size={14} stroke={3} color="#fff" />
        </div>
        {msg}
      </div>
    </div>
  );
}

export function App() {
  // auth
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'anon' | 'authed'
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [authError, setAuthError] = useState('');

  // workspace data
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState(TEMPLATES);
  const [defaultTemplateId, setDefaultTemplateId] = useState('t_service');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // navigation / ui
  const [tab, setTab] = useState('today');
  const [stack, setStack] = useState([]);
  const [toast, setToast] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);

  const loaded = useRef(false);

  // ── auth bootstrap ────────────────────────────────────────
  useEffect(() => {
    // surface ?auth_error= coming back from the Google redirect
    const params = new URLSearchParams(window.location.search);
    const e = params.get('auth_error');
    if (e) { setAuthError(e); window.history.replaceState({}, '', window.location.pathname); }

    (async () => {
      try {
        const res = await api.me();
        setGoogleEnabled(!!res.googleEnabled);
        applyUser(res.user);
        await hydrate();
        setAuthState('authed');
      } catch {
        try { const c = await api.config(); setGoogleEnabled(!!c.googleEnabled); } catch { /* ignore */ }
        setAuthState('anon');
      }
    })();
  }, []);

  function applyUser(user) {
    if (user?.name) COMPANY.user = user.name; // personalize header avatar + profile
  }

  async function hydrate() {
    const s = await api.getState();
    loaded.current = false;
    setJobs(s.jobs || []);
    setTemplates(s.templates && s.templates.length ? s.templates : TEMPLATES);
    setDefaultTemplateId(s.defaultTemplateId || 't_service');
    setSettings({ ...DEFAULT_SETTINGS, ...(s.settings || {}) });
    loaded.current = true;
  }

  const onAuthed = async (user) => {
    applyUser(user);
    try { await hydrate(); } catch { /* ignore */ }
    setAuthState('authed');
  };

  const onSessionLost = (err) => {
    if (err instanceof AuthError) { loaded.current = false; setStack([]); setAuthState('anon'); }
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* ignore */ }
    loaded.current = false;
    setJobs([]); setStack([]); setTab('today');
    setAuthState('anon');
  };

  // ── debounced persistence (server is the source of truth) ─
  const savers = useRef(null);
  if (!savers.current) {
    savers.current = {
      jobs: debounce((v) => api.saveJobs(v).catch(onSessionLost), 500),
      templates: debounce((v) => api.saveTemplates(v).catch(onSessionLost), 500),
      def: debounce((v) => api.saveDefaultTemplate(v).catch(onSessionLost), 500),
      settings: debounce((v) => api.saveSettings(v).catch(onSessionLost), 500),
    };
  }
  useEffect(() => { if (loaded.current) savers.current.jobs(jobs); }, [jobs]);
  useEffect(() => { if (loaded.current) savers.current.templates(templates); }, [templates]);
  useEffect(() => { if (loaded.current) savers.current.def(defaultTemplateId); }, [defaultTemplateId]);
  useEffect(() => { if (loaded.current) savers.current.settings(settings); }, [settings]);

  // ── domain actions (unchanged from the prototype) ─────────
  const jobById = (id) => jobs.find(j => j.id === id);
  const push = (s) => setStack(st => [...st, s]);
  const pop = () => setStack(st => st.slice(0, -1));
  const reset = () => setStack([]);
  const updateJob = (id, patch) => setJobs(js => js.map(j => j.id === id ? { ...j, ...patch } : j));
  const showToast = (m) => { setToast(m); clearTimeout(window.__toast); window.__toast = setTimeout(() => setToast(null), 2400); };

  const openJob = (job) => push({ type: 'detail', jobId: job.id });
  const onNavigate = (job) => push({ type: 'navigate', jobId: job.id });
  const onReport = (job) => push({ type: 'report', jobId: job.id });
  const openCreate = () => push({ type: 'create' });
  const openCreateAt = (date, startTime) => push({ type: 'create', date, startTime });
  const openTemplates = () => push({ type: 'templates' });
  const openArticles = () => push({ type: 'articles' });
  const openProfile = () => push({ type: 'profile' });
  const onMoveJob = (job, iso) => { if (iso && iso !== job.date) { updateJob(job.id, { date: iso }); showToast(`${job.title.split(' ')[0]} flyttad till ${fmtDateShort(iso)}`); } };
  const requestMove = (job) => setMoveTarget(job);
  const onEditJob = (job, patch) => { updateJob(job.id, patch); showToast('Uppdrag uppdaterat'); };

  const onCreateJob = (data, goReport) => {
    const list = Array.isArray(data) ? data : [data];
    const gid = list.length > 1 ? nextGroupId(jobs) : null;
    let cur = jobs;
    const created = [];
    list.forEach((d, i) => {
      const id = nextJobId(cur);
      const job = { ...d, id };
      if (gid) { job.groupId = gid; job.partIndex = i + 1; job.partTotal = list.length; }
      created.push(job);
      cur = [job, ...cur];
    });
    setJobs(js => [...created, ...js]);
    pop();
    if (goReport && created[0]) push({ type: 'report', jobId: created[0].id });
    showToast(list.length > 1 ? `Uppdrag skapat · ${list.length} besök` : (goReport ? 'Uppdrag skapat — rapportera nu' : 'Uppdrag skapat'));
  };

  // Save this report and book a linked follow-up visit in the same mission group.
  const onFollowUp = (job, report, fu) => {
    setJobs(js => {
      const gid = job.groupId || nextGroupId(js);
      let updated = js.map(j => j.id === job.id
        ? { ...j, report, status: j.status === 'fakturerad' ? 'fakturerad' : 'rapporterad', groupId: gid }
        : j);
      const followUp = {
        ...job, id: nextJobId(updated), groupId: gid,
        date: fu.date, startTime: fu.startTime, estMinutes: Math.round(fu.hours * 60),
        status: 'planerad', report: null, invoiceNo: undefined, invoicedAt: undefined, notifiedAt: undefined,
      };
      updated = [followUp, ...updated];
      const sorted = updated.filter(j => j.groupId === gid).sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime));
      const idxMap = {}; sorted.forEach((j, i) => idxMap[j.id] = i + 1);
      return updated.map(j => j.groupId === gid ? { ...j, partIndex: idxMap[j.id], partTotal: sorted.length } : j);
    });
    pop();
    showToast('Rapport sparad · uppföljning bokad ' + fmtDateShort(fu.date));
  };

  const onSaveReport = (job, report) => {
    const nextStatus = job.status === 'fakturerad' ? 'fakturerad' : 'rapporterad';
    updateJob(job.id, { report, status: nextStatus });
    pop();
    showToast(nextStatus === 'fakturerad' ? 'Rapport sparad' : 'Uppdrag rapporterat');
  };

  const onInvoice = (job) => {
    updateJob(job.id, { status: 'fakturerad', invoiceNo: nextInvoiceNo(jobs), invoicedAt: TODAY });
    showToast('Markerad som fakturerad');
    setStack(st => st.length && st[st.length - 1].type === 'detail' ? st.slice(0, -1) : st);
  };

  const onNotify = (job) => {
    updateJob(job.id, { notifiedAt: nowTime() });
    showToast(`Meddelande skickat till ${job.customer}`);
  };

  // Mirror the user's saved settings into the shared module singletons the screens read
  // directly (same pattern as COMPANY.user on login). Cheap + idempotent each render.
  setArticles(settings.articles);
  setVat(settings.vat != null ? settings.vat : 0.25);
  const syncedCompany = { ...(settings.company || {}) };
  if (settings.baseAddress) syncedCompany.base = settings.baseAddress;
  if (Object.keys(syncedCompany).length) setCompany(syncedCompany);

  // ── render gate ───────────────────────────────────────────
  if (authState === 'loading') return <div className="gv-loading">Laddar…</div>;
  if (authState === 'anon') return <AuthScreen onAuthed={onAuthed} googleEnabled={googleEnabled} initialError={authError} />;

  let content;
  if (stack.length) {
    const top = stack[stack.length - 1];
    const job = top.jobId ? jobById(top.jobId) : null;
    if (top.type === 'create') {
      content = <CreateFlow onClose={pop} onCreate={onCreateJob} templates={templates} defaultTemplateId={defaultTemplateId} initialDate={top.date} initialTime={top.startTime} jobs={jobs} settings={settings} />;
    } else if (top.type === 'templates') {
      content = <TemplatesScreen templates={templates} setTemplates={setTemplates} defaultId={defaultTemplateId} setDefault={(id) => { setDefaultTemplateId(id); showToast('Standardmall uppdaterad'); }} onBack={pop} />;
    } else if (top.type === 'articles') {
      content = <ArticlesScreen articles={(settings.articles && settings.articles.length) ? settings.articles : ARTICLES} setArticles={(list) => setSettings((s) => ({ ...s, articles: list }))} onBack={pop} />;
    } else if (top.type === 'profile') {
      content = <ProfileScreen jobs={jobs} templates={templates} defaultId={defaultTemplateId} onOpenTemplates={openTemplates} onOpenArticles={openArticles} onBack={pop} settings={settings} setSettings={setSettings} showToast={showToast} onLogout={logout} />;
    } else if (!job) {
      content = <div className="gv-app" />;
    } else if (top.type === 'detail') {
      content = <JobDetail job={job} jobs={jobs} onOpenPart={(pj) => push({ type: 'detail', jobId: pj.id })} onBack={pop} onNavigate={() => onNavigate(job)} onReport={onReport} onInvoice={onInvoice} onNotify={onNotify} onEdit={onEditJob} />;
    } else if (top.type === 'navigate') {
      content = <NavigateScreen job={job} onBack={pop} onArrive={() => { pop(); showToast('Framme — kör igång!'); }} />;
    } else if (top.type === 'report') {
      const tpl = templates.find(t => t.id === (job.templateId || defaultTemplateId)) || templates[0];
      content = <ReportScreen job={job} template={tpl} templates={templates} onBack={pop} onSave={(rep) => onSaveReport(job, rep)} onFollowUp={(rep, fu) => onFollowUp(job, rep, fu)} settings={settings} jobs={jobs} />;
    }
  } else {
    let screen;
    if (tab === 'today') screen = <TodayScreen jobs={jobs} onOpen={openJob} onOpenProfile={openProfile} onMove={requestMove} />;
    else if (tab === 'calendar') screen = <CalendarScreen jobs={jobs} onOpen={openJob} onOpenProfile={openProfile} onMove={requestMove} />;
    else if (tab === 'invoice') screen = <InvoiceScreen jobs={jobs} onOpen={openJob} onInvoice={onInvoice} onOpenProfile={openProfile} />;
    else screen = <FreeSlotsScreen jobs={jobs} onOpenProfile={openProfile} onBook={openCreateAt} settings={settings} />;
    content = (
      <div className="gv-app">
        {screen}
        <TabBar active={tab} onTab={(t) => { reset(); setTab(t); }} onCreate={openCreate} />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', position: 'relative' }}>
      {content}
      <Toast msg={toast} />
      <SlotPickerSheet
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        title="Omplanera uppdrag"
        subtitle={moveTarget ? `${moveTarget.title} · ${moveTarget.customer}` : ''}
        jobs={jobs}
        settings={settings}
        excludeId={moveTarget ? moveTarget.id : null}
        defaultHours={moveTarget ? (moveTarget.estMinutes || 60) / 60 : 2}
        ctaLabel="Flytta hit"
        onPick={(iso, startHM, hrs) => {
          if (moveTarget) {
            updateJob(moveTarget.id, { date: iso, startTime: startHM, estMinutes: Math.round(hrs * 60) });
            showToast(`${moveTarget.title.split(' ')[0]} omplanerad till ${fmtDateShort(iso)} ${startHM}`);
          }
          setMoveTarget(null);
        }}
      />
    </div>
  );
}
